import {
  Activity,
  AppWindow,
  Boxes,
  Eye,
  EyeOff,
  FileCheck2,
  GitBranch,
  Inbox,
  KeyRound,
  LayoutDashboard,
  Mail,
  Menu,
  ScrollText,
  Settings,
  Share2,
  Waypoints,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { NotSignedIn, startSignIn } from '../api/client.js';
import { useMe, useSetMenuHints } from '../api/queries.js';
import { UserMenu } from '../components/UserMenu.js';
import { Tooltip } from '../components/ui.jsx';
import { APP_VERSION } from '../release.jsx';

/**
 * The frame every page sits in: navigation and who is signed in. Whether the live stream is
 * connected is said where it matters — on Dashboard and Live Traffic, next to the numbers it
 * explains — and not in a corner of the sidebar on every page.
 *
 * The shape is the one Message and Security use: sectioned sidebar, app bar with the user menu,
 * and below 1000px a panel that slides over the content.
 */

interface Area {
  to: string;
  label: string;
  hint: string;
  icon: LucideIcon;
  end?: boolean;
}

const SECTIONS: Array<{ label: string; items: Area[] }> = [
  {
    label: 'OSSERVA',
    items: [
      { to: '/', label: 'Dashboard', hint: 'Le ultime 24 ore', icon: LayoutDashboard, end: true },
      { to: '/live', label: 'Live Traffic', hint: 'I messaggi adesso', icon: Activity },
      { to: '/messages', label: 'Messages', hint: 'Storico e ricerca', icon: Mail },
      { to: '/correlations', label: 'Correlations', hint: 'La catena causale', icon: GitBranch },
      { to: '/dead-letters', label: 'Dead Letters', hint: 'Quello che nessuno ha preso', icon: Inbox },
    ],
  },
  {
    label: 'CONFIGURA',
    items: [
      { to: '/applications', label: 'Applications', hint: 'Identità e credenziali', icon: AppWindow },
      { to: '/channels', label: 'Channels', hint: 'Dove si pubblica', icon: Boxes },
      { to: '/permissions', label: 'Permissions', hint: 'Chi può cosa', icon: KeyRound },
      { to: '/contracts', label: 'Contracts', hint: 'La forma promessa', icon: FileCheck2 },
      { to: '/providers', label: 'Providers', hint: 'Il trasporto sotto', icon: Share2 },
    ],
  },
  {
    label: 'SISTEMA',
    items: [
      { to: '/audit', label: 'Audit', hint: 'Il registro dei fatti', icon: ScrollText },
      { to: '/settings', label: 'Settings', hint: 'Proiezioni e integrità', icon: Settings },
    ],
  },
];

export function Shell() {
  const me = useMe();
  const setMenuHints = useSetMenuHints();
  const location = useLocation();

  // On narrow screens the menu is a panel over the content: it closes when the page changes,
  // otherwise it would stay open over the page just chosen.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.classList.toggle('menu-open', menuOpen);
    if (!menuOpen) return () => document.body.classList.remove('menu-open');
    const escape = (event: KeyboardEvent) => event.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('keydown', escape);
      document.body.classList.remove('menu-open');
    };
  }, [menuOpen]);

  // What the last attempt to sign in reported, read once: the address is cleaned immediately so a
  // reload does not take the same decision again on stale information.
  const [ssoError] = useState(() => {
    const parameters = new URLSearchParams(window.location.search);
    const reason = parameters.get('sso_error');
    if (reason === null) return null;
    parameters.delete('sso_error');
    const search = parameters.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
    return reason;
  });

  // A session that ended is not an error page: Security decides whether the user comes straight
  // back in. The silent attempt keeps an ordinary reload from looking like a sign-in.
  //
  // It is tried exactly once. A silent attempt that comes back `login_required` means Security
  // has no session to reuse, so the only thing that can still work is asking for the real sign-in
  // — trying quietly again would land here unchanged, and every lap spends one of the thirty
  // authorization requests a minute Security allows.
  useEffect(() => {
    if (!(me.error instanceof NotSignedIn)) return;
    if (ssoError === null) startSignIn(true);
    else if (ssoError === 'login_required') startSignIn(false);
  }, [me.error, ssoError]);

  if (me.isLoading) return <Splash>Apro Broker…</Splash>;
  if (me.error instanceof NotSignedIn && ssoError !== null && ssoError !== 'login_required') {
    return (
      <Splash>
        Security ha rifiutato l&apos;accesso: <code>{ssoError}</code>.{' '}
        <button type="button" className="link" onClick={() => startSignIn(false)}>
          Riprova
        </button>
      </Splash>
    );
  }
  if (me.error) return <Splash>Ti sto portando su Security per l&apos;accesso…</Splash>;

  // Until `/auth/me` has answered the hints are shown: the newcomer is the one who needs them.
  const hints = me.data?.preferences.menuHints ?? true;

  return (
    <div className={`shell${menuOpen ? ' menu-open' : ''}${hints ? '' : ' no-hints'}`}>
      <button type="button" className="menu-scrim" aria-hidden={!menuOpen} tabIndex={-1} onClick={() => setMenuOpen(false)} />

      <aside className="sidebar" id="app-menu">
        <div className="brand">
          <div className="brand-mark">
            <Waypoints size={18} />
          </div>
          <div className="brand-name">
            <strong>Broker</strong>
            <small>Messaging fabric</small>
          </div>
          <Tooltip placement="bottom" label={hints ? 'Nascondi le spiegazioni del menu' : 'Mostra le spiegazioni del menu'}>
            <button
              type="button"
              className="hints-toggle"
              aria-pressed={hints}
              aria-label={hints ? 'Nascondi le spiegazioni del menu' : 'Mostra le spiegazioni del menu'}
              onClick={() => setMenuHints.mutate(!hints)}
            >
              {hints ? <Eye size={16} /> : <EyeOff size={16} />}
            </button>
          </Tooltip>
        </div>

        {SECTIONS.map((section) => (
          <nav key={section.label} aria-label={section.label}>
            <span className="nav-label">{section.label}</span>
            {section.items.map((area) => (
              <NavLink key={area.to} to={area.to} end={area.end} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                <span className="nav-icon">
                  <area.icon size={17} />
                </span>
                <span className="nav-copy">
                  <strong>{area.label}</strong>
                  {hints && <small>{area.hint}</small>}
                </span>
              </NavLink>
            ))}
          </nav>
        ))}
      </aside>

      <main className="main">
        <header className="appbar">
          <div className="page-inner">
            <button
              type="button"
              className="menu-toggle"
              aria-label={menuOpen ? 'Chiudi il menu' : 'Apri il menu'}
              aria-controls="app-menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <X size={19} /> : <Menu size={19} />}
            </button>
            <UserMenu />
          </div>
        </header>
        <Outlet />
      </main>

      <footer className="release-footer">
        <span>v{APP_VERSION}</span>
        <Link to="/release">Note di rilascio</Link>
      </footer>
    </div>
  );
}

function Splash({ children }: { children: ReactNode }) {
  return (
    <div className="splash">
      <div className="brand-mark large">
        <Waypoints size={26} />
      </div>
      <p>{children}</p>
    </div>
  );
}
